import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';
import type { ContractionPhase } from '../types';
import { PHASES } from '../types';

const PHASE_KEY_MAP: Record<ContractionPhase, string> = {
  early_latent: 'earlyLatent',
  late_latent: 'lateLatent',
  active: 'active',
  transition: 'transition',
};

interface PhaseProgressBarProps {
  currentPhase: ContractionPhase;
  hasData: boolean;
}

export function PhaseProgressBar({ currentPhase, hasData }: PhaseProgressBarProps) {
  const { t } = useTranslation('contractionTimer');
  const phaseConfig = PHASES.find((p) => p.phase === currentPhase) || PHASES[0];

  if (!hasData) {
    return (
      <div className="bg-white/10 rounded-[15px] p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white/70">
            {t('phaseProgress.title')}
          </span>
          <span className="text-sm text-white/50">
            {t('phaseProgress.noData')}
          </span>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white/30 w-0 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 rounded-[15px] p-3">
      <div className="flex items-center justify-between mb-2">
        <span className={cn('text-sm font-medium', phaseConfig.color)}>
          {t(`phases.${PHASE_KEY_MAP[phaseConfig.phase]}`)}
        </span>
        <span className="text-sm text-white/70">
          {t(`phases.${PHASE_KEY_MAP[phaseConfig.phase]}Hint`)}
        </span>
      </div>
      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            phaseConfig.bgColor
          )}
          style={{ width: `${phaseConfig.progressPercent}%` }}
        />
      </div>
      {/* Phase indicators */}
      <div className="flex justify-between mt-1.5 px-0.5">
        {PHASES.map((phase) => (
          <div
            key={phase.phase}
            className={cn(
              'w-2 h-2 rounded-full transition-all',
              phase.phase === currentPhase
                ? phase.bgColor
                : 'bg-white/30'
            )}
            title={t(`phases.${PHASE_KEY_MAP[phase.phase]}`)}
          />
        ))}
      </div>
    </div>
  );
}
